import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Spin, Empty, Typography } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getFieldIcon } from '../../../components/Forecast/icons';
import { WindDirectionIcon } from '../../../components/Forecast/WindDirectionIcon';
import { useEventSelection } from '../../../contexts/useEventSelection';
import { useObservationFields } from '../../../hooks/useObservationFields';
import { usePlayerOptional } from '../usePlayerContext';
import { useStationObservations } from '../useStationObservations';
import { useTheme } from '../../../contexts/useTheme';
import type { WidgetProps } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

export const StationObservationsWidget = ({
  config,
  sizeClass,
  playerTimestamp,
}: WidgetProps) => {
  const { selectedEvent } = useEventSelection();
  const { getVisibleFields, loading: isLoadingFields } = useObservationFields();
  const { theme } = useTheme();
  const player = usePlayerOptional();
  const { stations: allStations, loading, error } = useStationObservations();
  const isDark = theme === 'dark';

  const selectedDateRange = player?.selectedDateRange ?? null;
  const configuredStations = useMemo(
    () => (config.selectedStations as string[] | undefined) ?? [],
    [config.selectedStations],
  );
  const columnCount = (config.columns as number) ?? 2;

  const [selectedStationIndex, setSelectedStationIndex] = useState(0);

  const stations = useMemo(() => {
    if (configuredStations.length === 0) return allStations;
    return allStations.filter((s) =>
      configuredStations.includes(s.station_reference),
    );
  }, [allStations, configuredStations]);

  useEffect(() => {
    setSelectedStationIndex(0);
  }, [selectedEvent]);

  useEffect(() => {
    if (selectedStationIndex >= stations.length && stations.length > 0) {
      setSelectedStationIndex(0);
    }
  }, [stations.length, selectedStationIndex]);

  const station = stations[selectedStationIndex] ?? null;
  const isDemo = selectedEvent?.demo ?? false;

  const targetTime = useMemo(() => {
    if (playerTimestamp) return playerTimestamp.getTime();
    if (station?.observations?.length) {
      const last = station.observations[station.observations.length - 1];
      return new Date(last.reading_timestamp).getTime();
    }
    return Date.now();
  }, [playerTimestamp, station]);

  const closestObs = useMemo(() => {
    if (!station?.observations?.length) return null;
    let best: (typeof station.observations)[0] | null = null;
    let bestTime = -Infinity;

    for (const obs of station.observations) {
      const t = new Date(obs.reading_timestamp).getTime();
      if (t <= targetTime && t > bestTime) {
        best = obs;
        bestTime = t;
      }
    }
    return best;
  }, [station, targetTime]);

  const availableFieldNames = useMemo(
    () => station?.reading_config?.map((cfg) => cfg.name) ?? [],
    [station],
  );

  const visibleFields = useMemo(() => {
    return getVisibleFields(isDemo)
      .filter((field) => availableFieldNames.includes(field.name))
      .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
  }, [getVisibleFields, isDemo, availableFieldNames]);

  const useTwoColumns = columnCount === 2;

  const rows = useMemo(() => {
    if (useTwoColumns) {
      const result: Array<{
        left: (typeof visibleFields)[0] | null;
        right: (typeof visibleFields)[0] | null;
      }> = [];
      for (let i = 0; i < visibleFields.length; i += 2) {
        result.push({
          left: visibleFields[i] || null,
          right: visibleFields[i + 1] || null,
        });
      }
      return result;
    }
    return visibleFields.map((f) => ({ left: f, right: null }));
  }, [visibleFields, useTwoColumns]);

  const observationTime = useMemo(() => {
    if (!closestObs) return null;
    return dayjs(closestObs.reading_timestamp).tz(
      selectedEvent?.circuit?.time_zone || 'UTC',
    );
  }, [closestObs, selectedEvent?.circuit?.time_zone]);

  const getReading = useCallback(
    (name: string): number | null => {
      if (!station || !closestObs) return null;
      const index = station.reading_config.findIndex(
        (cfg) => cfg.name === name,
      );
      return index >= 0 ? closestObs.readings[index] : null;
    },
    [station, closestObs],
  );

  const windIconSize = sizeClass === 'compact' ? 10 : 14;

  const formatValue = useCallback(
    (
      field: (typeof visibleFields)[0],
      value: number | null,
    ): string | React.ReactNode => {
      if (value === null) return 'N/A';

      if (field.name === 'windDirection') {
        const windDir = closestObs?.car_wind_direction;
        if (windDir === null || windDir === undefined) return 'N/A';
        const windSpeedValue = getReading('windSpeed');
        const windSpeedKmh =
          windSpeedValue !== null ? windSpeedValue * 3.6 : null;
        return (
          <WindDirectionIcon
            direction={windDir}
            windSpeed={windSpeedKmh}
            size={windIconSize}
            showAngle={sizeClass !== 'compact'}
          />
        );
      }

      if (field.name === 'windSpeed' || field.name === 'windGust') {
        const kmhValue = value * 3.6;
        if (field.name === 'windGust' && kmhValue <= 0) return 'N/A';
        return `${kmhValue.toFixed(1)} km/h`;
      }

      if (
        (field.name === 'rainIntensity' ||
          field.name === 'precipitationIntensity') &&
        value === 0
      ) {
        return 'N/A';
      }

      const unit = field.unit || '';
      let decimals = 1;
      if (unit === '%') decimals = 0;
      if (unit === 'lux') decimals = 0;

      return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
    },
    [closestObs, getReading, windIconSize, sizeClass],
  );

  if ((loading || isLoadingFields) && allStations.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>No event selected</span>}
        />
      </div>
    );
  }

  if (!selectedDateRange) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontSize: 11 }}>Select a time range on the timeline</span>}
        />
      </div>
    );
  }

  if (error || stations.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 11 }}>
              {error || 'No station data available'}
            </span>
          }
        />
      </div>
    );
  }

  if (!closestObs || !station) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 11 }}>No observations found</span>
          }
        />
      </div>
    );
  }

  const isCompact = sizeClass === 'compact';
  const isFull = !isCompact;
  const borderColor = isDark
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.06)';
  const mutedColor = isDark
    ? 'rgba(255, 255, 255, 0.5)'
    : 'rgba(0, 0, 0, 0.45)';

  const fontSize = isCompact ? 9 : 11;
  const iconSize = isCompact ? 10 : 14;
  const rowPad = isCompact ? '1px 4px 1px 0' : '2px 6px 2px 0';
  const rowPadLast = isCompact ? '1px 0' : '2px 0';
  const iconGap = isCompact ? 2 : 3;

  const renderDataTable = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.left ? (
              <>
                <td style={{ padding: rowPad, whiteSpace: 'nowrap', fontSize }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: iconGap }}>
                    {getFieldIcon(row.left.name, { size: iconSize })} {row.left.display_name}
                  </span>
                </td>
                <td style={{ padding: useTwoColumns ? rowPad : rowPadLast, textAlign: 'right', fontWeight: 500, fontSize }}>
                  {formatValue(row.left, getReading(row.left.name))}
                </td>
              </>
            ) : (
              <><td /><td /></>
            )}
            {useTwoColumns && (
              row.right ? (
                <>
                  <td style={{ padding: rowPad, whiteSpace: 'nowrap', fontSize }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: iconGap }}>
                      {getFieldIcon(row.right.name, { size: iconSize })} {row.right.display_name}
                    </span>
                  </td>
                  <td style={{ padding: rowPadLast, textAlign: 'right', fontWeight: 500, fontSize }}>
                    {formatValue(row.right, getReading(row.right.name))}
                  </td>
                </>
              ) : (
                <><td /><td /></>
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isCompact ? '1px 4px' : '4px 10px',
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isCompact ? 4 : 6 }}>
          {!isCompact && (
            <Text strong style={{ fontSize: 11 }}>
              Station
            </Text>
          )}
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {stations.map((s, index) => (
              <Button
                key={s.station_reference}
                type={selectedStationIndex === index ? 'primary' : 'default'}
                size="small"
                onClick={() => setSelectedStationIndex(index)}
                style={{
                  minWidth: isCompact ? 18 : 22,
                  height: isCompact ? 16 : 20,
                  padding: '0 4px',
                  fontSize: isCompact ? 9 : 10,
                }}
              >
                {s.station_reference}
              </Button>
            ))}
          </div>
        </div>
        {observationTime && isFull && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            @ {observationTime.format('HH:mm')}
          </Text>
        )}
      </div>

      {/* Observation data */}
      <div
        style={{
          padding: isCompact ? '0 4px' : '2px 10px',
          lineHeight: isCompact ? 1.3 : 1.5,
          color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
        }}
      >
        {renderDataTable()}
      </div>

      {/* Footer -- only in full/big mode */}
      {isFull && (
        <div
          style={{
            padding: '2px 10px 4px',
            borderTop: `1px solid ${borderColor}`,
          }}
        >
          <Text type="secondary" style={{ fontSize: 9 }}>
            {station.observation_count} obs
            {playerTimestamp && (
              <span style={{ color: mutedColor, marginLeft: 6 }}>synced</span>
            )}
          </Text>
        </div>
      )}
    </div>
  );
};
